'use client'

import { resolveErrorMessage } from '@tebuto/functions/api'
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { clearWidgetSession, loadWidgetSession, storeWidgetSession } from './storage'
import type { WidgetSession } from './types'

export interface WidgetAuthContextValue {
    /** Public API base URL of the embedding widget — undefined outside the embed (apps use their env default) */
    apiUrl?: string
    session: WidgetSession | null
    isDetecting: boolean
    requestLoginCode: (email: string) => Promise<void>
    confirmLoginCode: (email: string, code: string) => Promise<WidgetSession>
    logout: () => void
}

export const WidgetAuthContext = createContext<WidgetAuthContextValue>({
    session: null,
    isDetecting: false,
    requestLoginCode: async () => {
        /* noop default */
    },
    confirmLoginCode: async () => {
        throw new Error('WidgetAuthProvider is not mounted')
    },
    logout: () => {
        /* noop default */
    }
})

interface WidgetAuthProviderProps {
    apiUrl: string
    children: ReactNode
}

export function WidgetAuthProvider({ apiUrl, children }: WidgetAuthProviderProps) {
    const [session, setSession] = useState<WidgetSession | null>(null)
    const [isDetecting, setIsDetecting] = useState(true)

    useEffect(() => {
        let cancelled = false

        ;(async () => {
            const stored = loadWidgetSession()
            if (!stored) {
                if (!cancelled) setIsDetecting(false)
                return
            }

            try {
                const response = await fetch(`${apiUrl}/widget-auth/me`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${stored.accessToken}` },
                    credentials: 'omit'
                })
                if (cancelled) return

                if (!response.ok) {
                    clearWidgetSession()
                    setSession(null)
                    return
                }

                const client = await response.json()
                const refreshed: WidgetSession = { ...stored, client }
                storeWidgetSession(refreshed)
                setSession(refreshed)
            } catch {
                if (cancelled) return
                clearWidgetSession()
                setSession(null)
            } finally {
                if (!cancelled) setIsDetecting(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [apiUrl])

    const requestLoginCode = useCallback(
        async (email: string) => {
            const response = await fetch(`${apiUrl}/widget-auth/request-login-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'omit',
                body: JSON.stringify({ email })
            })
            if (!response.ok) {
                const body = await response.json().catch(() => ({}))
                throw new Error(resolveErrorMessage(body))
            }
        },
        [apiUrl]
    )

    const confirmLoginCode = useCallback(
        async (email: string, code: string): Promise<WidgetSession> => {
            const response = await fetch(`${apiUrl}/widget-auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'omit',
                body: JSON.stringify({ email, code })
            })
            if (!response.ok) {
                const body = await response.json().catch(() => ({}))
                throw new Error(resolveErrorMessage(body))
            }
            const body = (await response.json()) as WidgetSession
            storeWidgetSession(body)
            setSession(body)
            return body
        },
        [apiUrl]
    )

    const logout = useCallback(() => {
        clearWidgetSession()
        setSession(null)
    }, [])

    const value = useMemo<WidgetAuthContextValue>(
        () => ({ apiUrl, session, isDetecting, requestLoginCode, confirmLoginCode, logout }),
        [apiUrl, session, isDetecting, requestLoginCode, confirmLoginCode, logout]
    )

    return <WidgetAuthContext.Provider value={value}>{children}</WidgetAuthContext.Provider>
}
